import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import jwt_decode from "jwt-decode";
import { useOrganization, useUsers } from "../../../components/Common/HelperComponents/GetDataFromStore";
import {
  addEvidenceLink,
  collectPages,
  completeEvidenceUpload,
  createAuditComment,
  createEvidenceUploadUrl,
  deleteAuditComment,
  deleteEvidence,
  getAudit,
  getAuditControl,
  getEvidenceDownloadUrl,
  listAuditComments,
  listAuditControlEvents,
  listAuditEvidence,
  listAudits,
  listAuditControls,
  listAuditTasks,
  transitionAuditControl,
  unwrapGet,
  unwrapPost,
  updateAuditControl,
  updateAuditTask,
} from "../../../helpers/auditHub_helper";
import ControlChecks from "./ControlAudit/ControlChecks";
import ControlRemediations from "./ControlAudit/ControlRemediations";
import ControlTasks from "./ControlAudit/ControlTasks";
import CreateTaskDialog from "./ControlAudit/CreateTaskDialog";
import RequestInfoDialog from "./ControlAudit/RequestInfoDialog";
import { useAuditRefresh } from "./AuditRefreshContext";
import { AUDIT_STATUS_LABEL, AUDIT_STATUSES, REVIEW_STATUSES } from "../AuditHub/constants";
import ActivityTimeline from "../AuditHub/components/ActivityTimeline";
import DueDateField from "../AuditHub/DueDateField";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_SUBTLE,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  cardShellSx,
  outlinedButtonSx,
  primaryButtonSx,
} from "../AuditHub/theme";
import { excludeMspUsers, formatUserLabel, formatUserName, formatDateTime, getInitials } from "../AuditHub/utils";

const norm = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

export const frameworkMatches = (compliance, framework) => {
  const target = norm(compliance);
  if (!target || !framework) return false;
  const key = String(framework.complianceKey || "");
  const keyBase = key.replace(/_(aws|azure|gcp|oci)$/i, "");
  return [framework.complianceKey, keyBase, framework.name].some((candidate) => norm(candidate) === target);
};

const sameText = (a, b) => String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();

const statusLabel = (value) =>
  REVIEW_STATUSES.find((item) => item.value === value)?.label || value || "—";

const formatBytes = (size) => {
  const n = Number(size);
  if (!Number.isFinite(n) || n < 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const initials = (email) => {
  const local = String(email || "").split("@")[0];
  const bits = local.split(/[.\s_-]+/).filter(Boolean);
  if (!bits.length) return "?";
  return bits.slice(0, 2).map((bit) => bit.slice(0, 1).toUpperCase()).join("");
};

async function loadScopeControls(auditId, auditScopeId) {
  const controls = [];
  let pageNo = 1;
  let total = Infinity;
  while (controls.length < total && pageNo <= 20) {
    const roster = unwrapGet(
      await listAuditControls(auditId, {
        auditScopeId,
        page: pageNo,
        size: 100,
      })
    );
    const items = Array.isArray(roster?.items) ? roster.items : [];
    controls.push(...items);
    total = Number(roster?.total);
    if (!Number.isFinite(total) || !items.length) break;
    pageNo += 1;
  }
  return controls;
}

const findScope = (detail, accountNumber, compliance) => {
  const accounts = Array.isArray(detail?.accounts) ? detail.accounts : [];
  for (const account of accounts) {
    if (account.accountNumber !== accountNumber) continue;
    const scope = (account.scopes || []).find((item) => frameworkMatches(compliance, item.framework));
    if (scope) return scope;
  }
  return null;
};

async function loadRoster(accountNumber, compliance) {
  const size = 100;
  let pageNo = 1;
  let total = Infinity;
  while ((pageNo - 1) * size < total) {
    const page = unwrapGet(
      await listAudits({
        status: "active",
        accountNumber,
        sort: "lastActivityAt",
        order: "desc",
        page: pageNo,
        size,
      })
    );
    total = Number(page?.total) || 0;
    const audits = Array.isArray(page?.items) ? page.items : [];
    for (const summary of audits) {
      const detail = unwrapGet(await getAudit(summary.auditId));
      const scope = findScope(detail, accountNumber, compliance);
      if (!scope) continue;
      const controls = await loadScopeControls(detail.auditId, scope.auditScopeId);
      return { audit: detail, controls };
    }
    if (!audits.length) break;
    pageNo += 1;
  }
  return { audit: null, controls: [] };
}

export function useAuditControlIndex(accountNumber, compliance, auditId, knownAudit) {
  const [state, setState] = useState({ status: "idle", audit: null, controls: [], error: null });
  const knownAuditRef = useRef(knownAudit);
  knownAuditRef.current = knownAudit;
  const scopeId =
    auditId && knownAudit ? findScope(knownAudit, accountNumber, compliance)?.auditScopeId || null : null;
  const auditReady = !auditId || Boolean(knownAudit);

  useEffect(() => {
    if (!accountNumber || !compliance) {
      setState({ status: "idle", audit: null, controls: [], error: null });
      return undefined;
    }
    // The page already loaded the assessment. Wait for it instead of requesting it again.
    if (auditId && !knownAuditRef.current) {
      setState({ status: "loading", audit: null, controls: [], error: null });
      return undefined;
    }
    const audit = knownAuditRef.current;
    const scope = auditId ? findScope(audit, accountNumber, compliance) : null;
    if (auditId && !scope?.auditScopeId) {
      setState({ status: "ready", audit, controls: [], error: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", audit: audit || null, controls: [], error: null });
    const load = auditId
      ? loadScopeControls(audit.auditId, scope.auditScopeId).then((controls) => ({ audit, controls }))
      : loadRoster(accountNumber, compliance);
    load
      .then((result) => {
        if (!cancelled) setState({ status: "ready", ...result, error: null });
      })
      .catch((error) => {
        if (!cancelled) {
          setState({
            status: "error",
            audit: null,
            controls: [],
            error: error?.message || "Unable to load the assessment roster.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountNumber, compliance, auditId, scopeId, auditReady]);

  // A verdict refresh replaces the assessment. Keep this copy current without loading the roster again.
  useEffect(() => {
    if (!knownAudit) return undefined;
    setState((prev) => {
      if (prev.status !== "ready" || !prev.audit || prev.audit === knownAudit) return prev;
      if (prev.audit.auditId !== knownAudit.auditId) return prev;
      return { ...prev, audit: knownAudit };
    });
    return undefined;
  }, [knownAudit]);

  const findControl = (controlId, title) =>
    state.controls.find(
      (item) => sameText(item.controlCode, controlId) || (title && sameText(item.controlTitle, title))
    ) || null;

  // Section counts and filters read the roster, so a reloaded control is merged back into it.
  const applyControl = useCallback((auditControlId, next) => {
    if (!auditControlId || !next?.reviewStatus) return;
    setState((prev) => {
      const index = prev.controls.findIndex((item) => item.auditControlId === auditControlId);
      if (index < 0) return prev;
      const current = prev.controls[index];
      const assigneeEmail = Object.prototype.hasOwnProperty.call(next, "assigneeEmail")
        ? next.assigneeEmail
        : current.assigneeEmail;
      if (current.reviewStatus === next.reviewStatus && current.assigneeEmail === assigneeEmail) return prev;
      const controls = prev.controls.slice();
      controls[index] = { ...current, reviewStatus: next.reviewStatus, assigneeEmail };
      return { ...prev, controls };
    });
  }, []);

  return { ...state, findControl, applyControl };
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem", color: AH_MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: AH_TEXT },
  "& .MuiInputBase-input, & .MuiSelect-select": { fontSize: "0.875rem", color: AH_TEXT },
  "& .MuiSvgIcon-root": { color: AH_MUTED },
};

/** Same blue as the Pair Account launch button. */
const blueButtonSx = {
  ...primaryButtonSx,
  backgroundColor: "#1976d2",
  color: AH_WHITE,
  "&:hover": { backgroundColor: "#1565c0", boxShadow: "none" },
};

const TERMINAL_STATUSES = [
  { value: "PASSED", label: "Pass", color: "#16a34a", tint: "#f0fdf4", border: "#bbf7d0", Icon: CheckCircleOutlineRoundedIcon },
  { value: "FAILED", label: "Fail", color: "#dc2626", tint: "#fef2f2", border: "#fecaca", Icon: HighlightOffRoundedIcon },
  { value: "NOT_APPLICABLE", label: "Not Applicable", color: "#1976d2", tint: "#eff6ff", border: "#bfdbfe", Icon: BlockRoundedIcon },
];
const TERMINAL_VALUES = TERMINAL_STATUSES.map((item) => item.value);

/** Completed and cancelled audits are frozen server-side (409 AUDIT_NOT_ACTIVE). */
const LOCKED_AUDIT_STATUSES = [AUDIT_STATUSES.COMPLETED, AUDIT_STATUSES.CANCELLED];
const AUDIT_LOCK_TONE = { color: "#6b7280", tint: "#f9fafb", border: "#e5e7eb" };

const verdictChipSx = (item, selected) => ({
  height: 34,
  borderRadius: "8px",
  fontSize: "0.8125rem",
  fontWeight: 600,
  px: 0.5,
  border: `1px solid ${selected ? item.color : item.border}`,
  backgroundColor: selected ? item.color : AH_WHITE,
  color: selected ? AH_WHITE : item.color,
  "& .MuiChip-icon": { color: "inherit", fontSize: 17, ml: 0.75 },
  "&.MuiChip-clickable:hover, &.Mui-focusVisible": {
    backgroundColor: selected ? item.color : item.tint,
  },
  "&.Mui-disabled": {
    opacity: 1,
    color: "#94a3b8",
    borderColor: AH_INPUT_BORDER,
    backgroundColor: "#f8fafc",
  },
});

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: AH_MUTED,
};

const itemCardSx = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  border: `1px solid ${AH_BORDER}`,
  borderRadius: "10px",
  px: 1.5,
  py: 1.25,
  backgroundColor: AH_WHITE,
};

const iconTileSx = {
  width: 32,
  height: 32,
  borderRadius: "8px",
  backgroundColor: AH_TRACK,
  color: AH_SUBTLE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const itemActionSx = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: `1px solid ${AH_INPUT_BORDER}`,
  backgroundColor: AH_WHITE,
  color: AH_SUBTLE,
  "&:hover": { backgroundColor: "#f1f5f9", color: "#334155" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9" },
  "& .MuiSvgIcon-root": { fontSize: 16 },
};

const itemDeleteSx = {
  ...itemActionSx,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": { backgroundColor: "#fee2e2", color: "#991b1b" },
  "&.Mui-disabled": { color: "#cbd5e1", borderColor: "#f1f5f9", backgroundColor: "#f8fafc" },
};

const dialogCloseXSx = {
  p: 0,
  m: 0,
  border: "none",
  background: "transparent",
  color: AH_WHITE,
  fontSize: 34,
  fontWeight: 900,
  lineHeight: 0.85,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  WebkitTextStroke: `0.6px ${AH_WHITE}`,
  "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
};

const dialogButtonBaseSx = {
  textTransform: "none",
  fontSize: "0.875rem",
  borderRadius: "8px",
  px: 2.5,
  py: 0.85,
  flexShrink: 0,
  boxShadow: "none",
};

const dialogGreyButtonSx = {
  ...dialogButtonBaseSx,
  fontWeight: 600,
  color: AH_WHITE,
  backgroundColor: "#6c757d",
  border: "1px solid #6c757d",
  "&:hover": { backgroundColor: "#5a6268", borderColor: "#5a6268", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: AH_INPUT_BORDER, color: "#94a3b8", borderColor: AH_INPUT_BORDER },
};

const dialogPrimaryButtonSx = {
  ...dialogButtonBaseSx,
  fontWeight: 700,
  color: AH_WHITE,
  backgroundColor: AH_ACCENT,
  "&:hover": { backgroundColor: AH_ACCENT_HOVER, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#fed7aa", color: AH_WHITE },
};

/** Mirrors DeleteAuditDialog so both delete confirmations look identical. */
const confirmButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const evidenceButtonSx = {
  ...outlinedButtonSx,
  height: 34,
  minHeight: 34,
  px: 1.5,
  fontSize: "0.8125rem",
  color: "#1976d2",
  borderColor: "#bfdbfe",
  "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 17 },
  "&:hover": { borderColor: "#1976d2", backgroundColor: "#eff6ff", boxShadow: "none" },
  "&.Mui-disabled": { color: "#94a3b8", borderColor: AH_BORDER },
};

const avatarSx = (size = 28) => ({
  width: size,
  height: size,
  borderRadius: "50%",
  backgroundColor: "#1976d2",
  color: AH_WHITE,
  fontSize: size < 28 ? 10 : 11,
  fontWeight: 700,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const showAllButtonSx = {
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8125rem",
  height: 34,
  px: 1.25,
  borderRadius: "8px",
  color: "#1976d2",
  "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 17 },
  "&:hover": { backgroundColor: "#eff6ff" },
  "&.Mui-disabled": { color: "#94a3b8" },
};

/** The control detail caps each child list at 200; these routes page through the rest. */
const FULL_LISTS = {
  evidence: { fetchPage: listAuditEvidence, truncatedKey: "evidenceTruncated" },
  comments: { fetchPage: listAuditComments, truncatedKey: "commentsTruncated" },
  tasks: { fetchPage: listAuditTasks, truncatedKey: "tasksTruncated" },
};
const EMPTY_FULL_LISTS = { evidence: null, comments: null, tasks: null };

const readCurrentUserEmail = () => {
  try {
    const raw = localStorage.getItem("authUser");
    return raw ? String(jwt_decode(JSON.parse(raw).idToken)?.email || "") : "";
  } catch (_error) {
    return "";
  }
};

const IMAGE_FILE = /\.(png|jpe?g|gif|webp|svg|bmp)$/i;

const safeLinkHref = (url) => {
  const value = String(url || "").trim();
  return /^https?:\/\//i.test(value) ? value : null;
};

const ControlAuditPanel = ({ roster, controlId, title }) => {
  const { users } = useUsers();
  const organization = useOrganization();
  const match = roster.findControl(controlId, title);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkSaving, setLinkSaving] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [taskOpen, setTaskOpen] = useState(false);
  const [requestInfoOpen, setRequestInfoOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [fullLists, setFullLists] = useState(EMPTY_FULL_LISTS);
  const [listLoading, setListLoading] = useState({});
  const currentUserEmail = useMemo(() => readCurrentUserEmail().toLowerCase(), []);
  const notifyAuditChanged = useAuditRefresh();
  const { applyControl } = roster;

  const userOptions = useMemo(
    () =>
      excludeMspUsers(users, organization)
        .filter((user) => user.email)
        .map((user) => ({ email: user.email, label: formatUserLabel(user) })),
    [users, organization]
  );

  const userNames = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((user) => {
      if (user?.email) map.set(String(user.email).toLowerCase(), formatUserName(user));
    });
    return map;
  }, [users]);

  const nameFor = (email) => userNames.get(String(email || "").toLowerCase()) || email || "";
  const avatarFor = (email) => {
    const name = userNames.get(String(email || "").toLowerCase());
    return name && name !== email ? getInitials(name) : initials(email);
  };

  /** API pages run oldest-first; the panel lists newest-first. */
  const fetchFullList = async (kind, auditControlId) => {
    const items = await collectPages((params) =>
      FULL_LISTS[kind].fetchPage(roster.audit.auditId, auditControlId, params)
    );
    return items.reverse();
  };

  const reload = async (auditControlId) => {
    const next = unwrapGet(await getAuditControl(roster.audit.auditId, auditControlId));
    const expanded = Object.keys(FULL_LISTS).filter((kind) => fullLists[kind]);
    const refreshed = {};
    await Promise.all(
      expanded.map(async (kind) => {
        refreshed[kind] = next?.[FULL_LISTS[kind].truncatedKey] ? await fetchFullList(kind, auditControlId) : null;
      })
    );
    setDetail(next);
    applyControl?.(auditControlId, next);
    if (expanded.length) setFullLists((prev) => ({ ...prev, ...refreshed }));
    setHistoryVersion((value) => value + 1);
  };

  const showFullList = async (kind) => {
    if (!match?.auditControlId || listLoading[kind]) return;
    setListLoading((prev) => ({ ...prev, [kind]: true }));
    setError(null);
    try {
      const items = await fetchFullList(kind, match.auditControlId);
      setFullLists((prev) => ({ ...prev, [kind]: items }));
    } catch (err) {
      setError(err?.message || "Unable to load the full list.");
    } finally {
      setListLoading((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const auditId = roster.audit?.auditId || null;
  const auditControlId = match?.auditControlId || null;

  useEffect(() => {
    if (!auditId || !auditControlId) {
      setDetail(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setFullLists(EMPTY_FULL_LISTS);
    getAuditControl(auditId, auditControlId)
      .then((res) => {
        if (cancelled) return;
        const next = unwrapGet(res);
        setDetail(next);
        applyControl?.(auditControlId, next);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Unable to load this assessment control.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [auditId, auditControlId, applyControl]);

  const run = async (work) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Request failed.");
    } finally {
      setBusy(false);
    }
  };

  if (roster.status === "loading") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading assessment workspace…</Typography>
      </Box>
    );
  }

  if (roster.status === "error") {
    return <Typography sx={{ fontSize: 13, color: "#b91c1c", pt: 1 }}>{roster.error}</Typography>;
  }

  if (!roster.audit) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748b", pt: 1 }}>
        No in-progress assessment includes this account and framework.
      </Typography>
    );
  }

  if (!match) {
    return (
      <Typography sx={{ fontSize: 13, color: "#64748b", pt: 1 }}>
        This control is not on the assessment roster.
      </Typography>
    );
  }

  const auditCode = roster.audit.auditCode || roster.audit.auditId;
  const fieldId = (name) => `audit-control-${name}-${match.auditControlId}`;
  const allowed = Array.isArray(detail?.allowedTransitions) ? detail.allowedTransitions : [];
  const reviewValue = detail?.reviewStatus || match.reviewStatus;
  const assigneeEmail = detail?.assigneeEmail ?? match.assigneeEmail ?? "";
  const assigneeKey = String(assigneeEmail).toLowerCase();
  const knownAssignee = assigneeKey ? userOptions.find((user) => user.email.toLowerCase() === assigneeKey) : null;
  // The API may store the email in another case, or name someone outside the pickable list; show them anyway.
  const assigneeName = nameFor(assigneeEmail);
  const assigneeValue =
    knownAssignee ||
    (assigneeKey
      ? { email: assigneeEmail, label: assigneeName !== assigneeEmail ? `${assigneeName} (${assigneeEmail})` : assigneeEmail }
      : null);
  const assigneeOptions = assigneeValue && !knownAssignee ? [assigneeValue, ...userOptions] : userOptions;
  const evidence = fullLists.evidence || (Array.isArray(detail?.evidence) ? detail.evidence : []);
  const comments = fullLists.comments || (Array.isArray(detail?.comments) ? detail.comments : []);
  const tasks = fullLists.tasks || (Array.isArray(detail?.tasks) ? detail.tasks : []);
  const reviewOptions = allowed.filter((value) => !TERMINAL_VALUES.includes(value));
  const frozenStatus = TERMINAL_STATUSES.find((item) => item.value === reviewValue) || null;
  const recordFrozen = Boolean(frozenStatus);
  const auditLockStatus = LOCKED_AUDIT_STATUSES.includes(roster.audit.status) ? roster.audit.status : null;
  const locked = recordFrozen || Boolean(auditLockStatus);
  // A decided control stays read-only; only Review status can move it back to an open status the API allows.
  const canReopen = recordFrozen && !auditLockStatus && reviewOptions.length > 0;
  const lockTone = auditLockStatus ? AUDIT_LOCK_TONE : frozenStatus;
  const auditDue = (roster.audit?.dueDate || "").slice(0, 10);
  // An unset control due date follows the assessment due date until the owner or an auditor sets another date.
  const storedSource = detail ? detail.dueDate : match.dueDate;
  const storedDue = String(storedSource || "").slice(0, 10);
  const dueDateValue = storedDue || auditDue;
  const auditorEmails = (Array.isArray(roster.audit.auditorEmails) ? roster.audit.auditorEmails : [])
    .filter(Boolean)
    .map((email) => String(email).toLowerCase());
  const dueDateEditors = [roster.audit.ownerEmail, ...auditorEmails]
    .filter(Boolean)
    .map((email) => String(email).toLowerCase());
  const isOwnerOrAuditor = Boolean(currentUserEmail) && dueDateEditors.includes(currentUserEmail);
  const isAuditor = Boolean(currentUserEmail) && auditorEmails.includes(currentUserEmail);
  const canEditDueDate = isOwnerOrAuditor;
  const canEditAssignee = isOwnerOrAuditor;
  const canEditReviewStatus = isOwnerOrAuditor || (Boolean(assigneeKey) && assigneeKey === currentUserEmail);

  const transition = (toStatus) => {
    if (TERMINAL_VALUES.includes(toStatus) && !isAuditor) return undefined;
    if (!canEditReviewStatus) return undefined;
    if (locked && !(canReopen && reviewOptions.includes(toStatus))) return undefined;
    if (toStatus === "NEEDS_MORE_INFO") {
      setRequestInfoOpen(true);
      return undefined;
    }
    const scoreChanges = TERMINAL_VALUES.includes(toStatus) || TERMINAL_VALUES.includes(reviewValue);
    return run(async () => {
      unwrapPost(await transitionAuditControl(roster.audit.auditId, match.auditControlId, { toStatus }));
      // Pass, Fail and Not Applicable move the score. Other review statuses do not.
      if (scoreChanges) notifyAuditChanged();
    });
  };

  const savePatch = (body) => {
    if (locked) return undefined;
    return run(async () => {
      unwrapPost(await updateAuditControl(roster.audit.auditId, match.auditControlId, body));
    });
  };

  const saveDueDate = (next) => {
    if (!canEditDueDate) return undefined;
    const explicit = next && next !== auditDue ? next : "";
    if (explicit === storedDue) return undefined;
    return savePatch({ dueDate: explicit || null });
  };

  const onUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await run(async () => {
      const created = unwrapPost(
        await createEvidenceUploadUrl(roster.audit.auditId, match.auditControlId, {
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          sizeBytes: file.size,
        })
      );
      const upload = await fetch(created.uploadUrl, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });
      if (!upload.ok) throw new Error("The file could not be uploaded.");
      unwrapPost(await completeEvidenceUpload(roster.audit.auditId, match.auditControlId, created.evidenceId));
    });
  };

  const closeLinkDialog = () => {
    if (linkSaving) return;
    setLinkOpen(false);
    setLinkUrl("");
    setLinkLabel("");
    setLinkError(null);
  };

  const onAddLink = async () => {
    const url = linkUrl.trim();
    const label = linkLabel.trim();
    if (!url || !label || linkSaving) return;
    setLinkSaving(true);
    setLinkError(null);
    try {
      unwrapPost(await addEvidenceLink(roster.audit.auditId, match.auditControlId, { linkUrl: url, linkLabel: label }));
    } catch (err) {
      setLinkError(err?.message || "Unable to add this link.");
      setLinkSaving(false);
      return;
    }
    setLinkSaving(false);
    setLinkOpen(false);
    setLinkUrl("");
    setLinkLabel("");
    try {
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Unable to refresh this assessment control.");
    }
  };

  const closeDeleteDialog = () => {
    if (deleteSaving) return;
    setPendingDelete(null);
    setDeleteError(null);
  };

  const deleteNoun = pendingDelete?.kind === "comment" ? "comment" : pendingDelete?.isLink ? "link" : "artifact";

  const confirmDelete = async () => {
    if (!pendingDelete?.id || deleteSaving) return;
    setDeleteSaving(true);
    setDeleteError(null);
    try {
      if (pendingDelete.kind === "comment") {
        unwrapPost(await deleteAuditComment(roster.audit.auditId, pendingDelete.id));
      } else {
        unwrapPost(await deleteEvidence(roster.audit.auditId, pendingDelete.id));
      }
    } catch (err) {
      setDeleteError(err?.message || `Unable to delete this ${deleteNoun}.`);
      setDeleteSaving(false);
      return;
    }
    setDeleteSaving(false);
    setPendingDelete(null);
    try {
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Unable to refresh this assessment control.");
    }
  };

  const onDownload = (evidenceId) =>
    run(async () => {
      const result = unwrapGet(await getEvidenceDownloadUrl(roster.audit.auditId, evidenceId));
      if (result?.downloadUrl) window.open(result.downloadUrl, "_blank", "noopener,noreferrer");
    });

  const onPost = () => {
    const body = comment.trim();
    if (!body) return;
    setComment("");
    run(async () => {
      unwrapPost(await createAuditComment(roster.audit.auditId, match.auditControlId, { body }));
    });
  };

  const onAnswered = (result) => {
    const saved = result?.check;
    if (!saved?.auditControlCheckId) return;
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            checks: (prev.checks || []).map((check) =>
              check.auditControlCheckId === saved.auditControlCheckId ? saved : check
            ),
            answeredCount: result.control?.answeredCount ?? prev.answeredCount,
            questionCount: result.control?.questionCount ?? prev.questionCount,
          }
        : prev
    );
    setHistoryVersion((value) => value + 1);
  };

  const onSetTaskStatus = (task, status) => {
    if (!canEditReviewStatus || locked || task.status === status) return undefined;
    return run(async () => {
      unwrapPost(await updateAuditTask(roster.audit.auditId, task.taskId, { status }));
    });
  };

  const onRequestInfoDone = async () => {
    setRequestInfoOpen(false);
    try {
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Unable to refresh this assessment control.");
    }
  };

  const onTaskCreated = async () => {
    setTaskOpen(false);
    try {
      await reload(match.auditControlId);
    } catch (err) {
      setError(err?.message || "Unable to refresh this assessment control.");
    }
  };

  const isOwnComment = (item) =>
    Boolean(currentUserEmail) && String(item.authorEmail || "").toLowerCase() === currentUserEmail;

  const renderShowAll = (kind, label) =>
    detail?.[FULL_LISTS[kind].truncatedKey] && !fullLists[kind] ? (
      <Button
        variant="text"
        disabled={Boolean(listLoading[kind])}
        onClick={() => showFullList(kind)}
        startIcon={listLoading[kind] ? <CircularProgress size={14} color="inherit" /> : <UnfoldMoreRoundedIcon />}
        sx={showAllButtonSx}
      >
        {listLoading[kind] ? "Loading..." : label}
      </Button>
    ) : null;

  const countLabel = (count, noun) => (Number.isFinite(count) ? `Show all ${count} ${noun}` : `Show all ${noun}`);
  const showAllComments = renderShowAll("comments", countLabel(detail?.commentCount, "comments"));

  return (
    <AuditHubThemeProvider>
    <Box sx={{ ...cardShellSx, ...auditHubPageSx, backgroundColor: AH_TRACK, p: { xs: 1.5, sm: 2.5 } }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mb: 0.75 }}>
            <Box
              component="span"
              sx={{ fontSize: 11, fontWeight: 600, color: AH_SUBTLE, backgroundColor: AH_WHITE, border: `1px solid ${AH_BORDER}`, borderRadius: "6px", px: 0.875, py: 0.25, lineHeight: 1.6 }}
            >
              {detail?.controlCode || match.controlCode}
            </Box>
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: AH_TEXT, lineHeight: 1.35 }}>
            {detail?.controlTitle || match.controlTitle}
          </Typography>
          {detail?.controlDescription ? (
            <Typography sx={{ fontSize: 13, color: AH_SUBTLE, mt: 0.5, lineHeight: 1.5 }}>{detail.controlDescription}</Typography>
          ) : null}
        </Box>
        <Tooltip
          title={!locked && !isAuditor ? "Only an Auditor can move a control to Pass/Fail/Not Applicable status." : ""}
          placement="top"
          disableInteractive
        >
        <Box role="radiogroup" aria-label="Terminal status" sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
          {TERMINAL_STATUSES.map((item) => {
            const selected = reviewValue === item.value;
            const enabled = isAuditor && !locked && allowed.includes(item.value) && !busy && !loading;
            const { Icon } = item;
            return (
              <Chip
                key={item.value}
                role="radio"
                aria-checked={selected}
                variant="outlined"
                label={item.label}
                icon={<Icon />}
                disabled={!selected && !enabled}
                onClick={!selected && enabled ? () => transition(item.value) : undefined}
                sx={verdictChipSx(item, selected)}
              />
            );
          })}
        </Box>
        </Tooltip>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {lockTone ? (
            <Box
              role="status"
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                border: `1px solid ${lockTone.border}`,
                backgroundColor: lockTone.tint,
                color: lockTone.color,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 18, flexShrink: 0 }} />
              <Typography sx={{ flex: "1 1 260px", minWidth: 0, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>
                {auditLockStatus ? "This assessment is" : "This control is marked"}{" "}
                <Box component="span" sx={{ fontWeight: 700, color: lockTone.color }}>
                  {auditLockStatus ? AUDIT_STATUS_LABEL[auditLockStatus] || auditLockStatus : frozenStatus.label}
                </Box>{" "}
                and is locked. Questions, evidence, tasks, comments and all fields are read-only.
                {canReopen && canEditReviewStatus ? " Change Review status to reopen it." : null}
              </Typography>
            </Box>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "minmax(0, 0.6fr) minmax(0, 1.4fr) repeat(2, minmax(0, 1fr))",
              },
              gap: 1.5,
              border: `1px solid ${AH_BORDER}`,
              borderRadius: "10px",
              backgroundColor: AH_WHITE,
              p: 1.5,
              mb: 2.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="label" sx={{ ...sectionLabelSx, display: "block", mb: 0.75 }}>Assessment ID</Typography>
              <Box
                sx={{ height: 40, display: "flex", alignItems: "center", px: 1.5, borderRadius: "8px", backgroundColor: AH_TRACK, fontSize: "0.875rem", fontWeight: 600, color: AH_TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {auditCode}
              </Box>
            </Box>
            <Tooltip
              title={!locked && !canEditAssignee ? "Only the assessment owner or an auditor of this assessment can change the assignee." : ""}
              placement="top"
              disableInteractive
            >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="label" htmlFor={fieldId("assignee")} sx={{ ...sectionLabelSx, display: "block", mb: 0.75 }}>Assigned to</Typography>
              <Autocomplete
                id={fieldId("assignee")}
                size="small"
                options={assigneeOptions}
                value={assigneeValue}
                disabled={busy || locked || !canEditAssignee}
                onChange={(_e, option) => (canEditAssignee ? savePatch({ assigneeEmail: option?.email || null }) : undefined)}
                getOptionLabel={(option) => option.label || option.email || ""}
                isOptionEqualToValue={(a, b) => a.email.toLowerCase() === b.email.toLowerCase()}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Unassigned"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: assigneeValue ? (
                        <Box sx={{ ...avatarSx(22), ml: 0.5 }}>{avatarFor(assigneeValue.email)}</Box>
                      ) : null,
                    }}
                    sx={fieldSx}
                  />
                )}
              />
            </Box>
            </Tooltip>
            <Tooltip
              title={!locked && !canEditReviewStatus ? "Only the assignee, the assessment owner, or an auditor of this assessment can change the review status." : ""}
              placement="top"
              disableInteractive
            >
            <Box sx={{ minWidth: 0 }}>
              <Typography component="label" htmlFor={fieldId("review-status")} sx={{ ...sectionLabelSx, display: "block", mb: 0.75 }}>Review status</Typography>
              <TextField
                id={fieldId("review-status")}
                select
                fullWidth
                size="small"
                value={reviewValue || ""}
                disabled={busy || !canEditReviewStatus || (locked && !canReopen) || !reviewOptions.length}
                onChange={(event) => transition(event.target.value)}
                sx={fieldSx}
              >
                {reviewValue && !reviewOptions.includes(reviewValue) ? (
                  <MenuItem value={reviewValue}>{statusLabel(reviewValue)}</MenuItem>
                ) : null}
                {reviewOptions.map((value) => (
                  <MenuItem key={value} value={value}>{statusLabel(value)}</MenuItem>
                ))}
              </TextField>
            </Box>
            </Tooltip>
            <Tooltip
              title={!locked && !canEditDueDate ? "Only the assessment owner or an auditor of this assessment can change the due date." : ""}
              placement="top"
              disableInteractive
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography component="label" htmlFor={fieldId("due-date")} sx={{ ...sectionLabelSx, display: "block", mb: 0.75 }}>Due date</Typography>
                <DueDateField
                  id={fieldId("due-date")}
                  value={dueDateValue}
                  onChange={saveDueDate}
                  maxDate={auditDue}
                  markedDate={auditDue}
                  disabled={busy || locked || !canEditDueDate}
                  allowClear={Boolean(storedDue) && storedDue !== auditDue}
                  fullWidth
                  showLabel={false}
                  size="small"
                  portalId="audit-hub-date-portal"
                />
              </Box>
            </Tooltip>
          </Box>

          <ControlChecks
            auditId={roster.audit.auditId}
            auditControlId={match.auditControlId}
            checks={detail?.checks}
            answeredCount={detail?.answeredCount}
            questionCount={detail?.questionCount}
            locked={locked}
            disabled={busy || !canEditReviewStatus}
            readOnlyHint={!locked && !canEditReviewStatus ? "Only the assignee, the assessment owner, or an auditor of this assessment can change the status." : ""}
            nameFor={nameFor}
            formatWhen={formatDateTime}
            onAnswered={onAnswered}
          />

          <ControlRemediations steps={detail?.remediationSteps} />

          <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Evidence & Artifacts</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
            {evidence.map((item) => {
              const isLink = item.kind === "LINK" || Boolean(item.linkUrl);
              const isImage = !isLink && IMAGE_FILE.test(item.fileName || "");
              const linkHref = isLink ? safeLinkHref(item.linkUrl) : null;
              const meta = isLink
                ? item.linkUrl
                : [
                    formatBytes(item.sizeBytes),
                    item.uploadedBy ? `Uploaded by ${nameFor(item.uploadedBy)}` : "",
                    formatDateTime(item.uploadedAt),
                    item.scanStatus,
                  ]
                    .filter(Boolean)
                    .join(" · ");
              return (
                <Box key={item.evidenceId} sx={itemCardSx}>
                  <Box sx={iconTileSx}>
                    {isLink ? <LinkOutlinedIcon /> : isImage ? <ImageOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: AH_TEXT }} noWrap title={isLink ? item.linkLabel || item.linkUrl : item.fileName}>
                      {isLink ? item.linkLabel || item.linkUrl : item.fileName || "File"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED }} noWrap title={meta}>
                      {meta}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.75, flexShrink: 0 }}>
                    {isLink ? (
                      <Tooltip title={linkHref ? "Open link in new tab" : "Only http(s) links can be opened"}>
                        <span>
                          <IconButton
                            component="a"
                            href={linkHref || undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            aria-label="Open link in new tab"
                            disabled={!linkHref}
                            sx={itemActionSx}
                          >
                            <OpenInNewOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Download">
                        <span>
                          <IconButton size="small" aria-label="Download evidence" disabled={busy} onClick={() => onDownload(item.evidenceId)} sx={itemActionSx}>
                            <FileDownloadOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Remove evidence"
                          disabled={busy || locked}
                          onClick={() => {
                            setDeleteError(null);
                            setPendingDelete({
                              kind: "evidence",
                              id: item.evidenceId,
                              isLink,
                              name: isLink ? item.linkLabel || item.linkUrl : item.fileName,
                            });
                          }}
                          sx={itemDeleteSx}
                        >
                          <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
            {!evidence.length ? <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No evidence yet.</Typography> : null}
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <Button variant="outlined" component="label" disabled={busy || locked} startIcon={<FileUploadOutlinedIcon />} sx={evidenceButtonSx}>
              Upload File
              <input hidden type="file" disabled={busy || locked} onChange={onUpload} />
            </Button>
            <Button variant="outlined" disabled={busy || locked} onClick={() => setLinkOpen(true)} startIcon={<LinkOutlinedIcon />} sx={evidenceButtonSx}>
              Add Link
            </Button>
            {renderShowAll("evidence", countLabel(detail?.evidenceCount, "items"))}
          </Box>

          <Box sx={{ borderTop: `1px solid ${AH_BORDER}`, my: 2.5 }} />

          <ControlTasks
            tasks={tasks}
            openTaskCount={detail?.openTaskCount}
            locked={locked}
            disabled={busy}
            statusLocked={!canEditReviewStatus}
            nameFor={nameFor}
            formatWhen={formatDateTime}
            onNewTask={() => setTaskOpen(true)}
            onSetStatus={onSetTaskStatus}
            footer={renderShowAll("tasks", "Show all tasks")}
          />

          <Box sx={{ borderTop: `1px solid ${AH_BORDER}`, my: 2.5 }} />

          <Typography sx={{ ...sectionLabelSx, mb: 1.25 }}>Activity & Comments</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1.5 }}>
            {comments.map((item) => (
              <Box key={item.commentId} sx={{ ...itemCardSx, alignItems: "flex-start", gap: 1.25 }}>
                <Box sx={avatarSx(28)}>{avatarFor(item.authorEmail)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", columnGap: 1.5 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT, overflowWrap: "anywhere" }}>
                      {nameFor(item.authorEmail)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: AH_MUTED, whiteSpace: "nowrap" }}>{formatDateTime(item.createdAt)}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "#374151", mt: 0.5, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                    {item.body}
                  </Typography>
                </Box>
                {isOwnComment(item) ? (
                  <Tooltip title="Delete">
                    <span>
                      <IconButton
                        size="small"
                        aria-label="Delete comment"
                        disabled={busy || locked}
                        onClick={() => {
                          setDeleteError(null);
                          setPendingDelete({ kind: "comment", id: item.commentId });
                        }}
                        sx={{ ...itemDeleteSx, flexShrink: 0 }}
                      >
                        <i className="mdi mdi-trash-can" style={{ fontSize: 15 }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : null}
              </Box>
            ))}
            {!comments.length ? <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No comments yet.</Typography> : null}
            {showAllComments ? <Box>{showAllComments}</Box> : null}
          </Box>
          <Box
            sx={{
              border: `1px solid ${AH_INPUT_BORDER}`,
              borderRadius: "10px",
              backgroundColor: busy || locked ? "#f8fafc" : AH_WHITE,
              "&:focus-within": { borderColor: AH_TEXT },
            }}
          >
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={8}
              variant="standard"
              placeholder="Add a comment or note..."
              value={comment}
              disabled={busy || locked}
              onChange={(event) => setComment(event.target.value)}
              inputProps={{ maxLength: 4000, "aria-label": "Add a comment" }}
              InputProps={{ disableUnderline: true }}
              sx={{ px: 1.5, pt: 1.25, "& .MuiInputBase-input": { fontSize: "0.875rem", color: AH_TEXT } }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pb: 1 }}>
              <Button
                variant="contained"
                disabled={busy || locked || !comment.trim()}
                onClick={onPost}
                startIcon={<SendRoundedIcon />}
                sx={{ ...blueButtonSx, height: 32, minHeight: 32, px: 1.75, fontSize: "0.8125rem" }}
              >
                Post
              </Button>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2.5,
              border: `1px solid ${AH_BORDER}`,
              borderRadius: "10px",
              backgroundColor: AH_WHITE,
              overflow: "hidden",
            }}
          >
            <Box
              component="button"
              type="button"
              aria-expanded={historyOpen}
              aria-controls={fieldId("history")}
              onClick={() => setHistoryOpen((value) => !value)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                px: 1.5,
                py: 1.25,
                m: 0,
                border: "none",
                backgroundColor: "#edf4fb",
                cursor: "pointer",
                textAlign: "left",
                "&:hover": { backgroundColor: "#e3eef8" },
                "&:focus-visible": { outline: `2px solid ${AH_ACCENT}`, outlineOffset: -2 },
              }}
            >
              <Typography component="span" sx={sectionLabelSx}>History</Typography>
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 12, fontWeight: 600, color: "#1976d2" }}>
                {historyOpen ? "Hide" : "Show"}
                <ExpandMoreRoundedIcon
                  sx={{ fontSize: 18, transition: "transform 0.2s ease", transform: historyOpen ? "rotate(180deg)" : "none" }}
                />
              </Box>
            </Box>
            {historyOpen ? (
              <Box id={fieldId("history")} sx={{ px: 1.5, pt: 1.5, pb: 1.25, borderTop: `1px solid ${AH_BORDER}` }}>
                <ActivityTimeline
                  fetchPage={(params) => listAuditControlEvents(roster.audit.auditId, match.auditControlId, params)}
                  resetKey={match.auditControlId}
                  refreshKey={historyVersion}
                  nameFor={nameFor}
                  emptyText="No history recorded for this control yet."
                />
              </Box>
            ) : null}
          </Box>
        </>
      )}

      {error ? <Typography sx={{ fontSize: 13, color: "#b91c1c", mt: 1.5 }}>{error}</Typography> : null}

      <Dialog
        open={linkOpen}
        onClose={closeLinkDialog}
        fullWidth
        maxWidth="xs"
        aria-labelledby={fieldId("add-link-title")}
        PaperProps={{
          component: "form",
          noValidate: true,
          onSubmit: (event) => {
            event.preventDefault();
            onAddLink();
          },
          sx: {
            ...auditHubPageSx,
            borderRadius: "12px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "none",
            border: `1px solid ${AH_INPUT_BORDER}`,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            backgroundColor: AH_ACCENT,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id={fieldId("add-link-title")}
              sx={{ fontWeight: 700, fontSize: { xs: "1.125rem", sm: "1.25rem" }, color: AH_WHITE, letterSpacing: "-0.02em", lineHeight: 1.2 }}
            >
              Add Link
            </Typography>
            <Typography sx={{ mt: 0.75, color: "rgba(255,255,255,0.9)", fontSize: "0.875rem" }}>
              Attach a web link as evidence for {match.controlCode || "this control"}.
            </Typography>
          </Box>
          <Box component="button" type="button" aria-label="Close" onClick={closeLinkDialog} disabled={linkSaving} sx={dialogCloseXSx}>
            <span aria-hidden="true">×</span>
          </Box>
        </Box>

        <DialogContent sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: AH_TRACK }}>
          <Box
            sx={{
              backgroundColor: AH_WHITE,
              border: `1px solid ${AH_INPUT_BORDER}`,
              borderRadius: "12px",
              p: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              id={fieldId("link-label")}
              label="Label"
              required
              autoFocus
              fullWidth
              size="small"
              value={linkLabel}
              disabled={linkSaving}
              onChange={(event) => {
                setLinkLabel(event.target.value);
                if (linkError) setLinkError(null);
              }}
              inputProps={{ maxLength: 200 }}
              sx={fieldSx}
            />
            <TextField
              id={fieldId("link-url")}
              label="URL"
              required
              fullWidth
              size="small"
              type="url"
              placeholder="https://example.com"
              value={linkUrl}
              disabled={linkSaving}
              onChange={(event) => {
                setLinkUrl(event.target.value);
                if (linkError) setLinkError(null);
              }}
              inputProps={{ maxLength: 2000 }}
              sx={fieldSx}
            />
          </Box>
        </DialogContent>

        <DialogActions
          disableSpacing
          sx={{
            px: 2.5,
            py: 1.5,
            gap: 1.5,
            flexWrap: { xs: "wrap", sm: "nowrap" },
            borderTop: `1px solid ${AH_INPUT_BORDER}`,
            bgcolor: AH_WHITE,
            justifyContent: "space-between",
          }}
        >
          <Button type="button" variant="contained" disableElevation onClick={closeLinkDialog} disabled={linkSaving} sx={dialogGreyButtonSx}>
            Cancel
          </Button>
          {linkError ? (
            <Typography
              role="alert"
              sx={{
                flex: 1,
                minWidth: 0,
                order: { xs: 3, sm: 0 },
                flexBasis: { xs: "100%", sm: "auto" },
                fontSize: "0.8125rem",
                lineHeight: 1.45,
                color: "#dc2626",
                textAlign: "center",
                overflowWrap: "anywhere",
              }}
            >
              {linkError}
            </Typography>
          ) : null}
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={linkSaving || !linkUrl.trim() || !linkLabel.trim()}
            startIcon={linkSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={dialogPrimaryButtonSx}
          >
            {linkSaving ? "Adding..." : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
        aria-labelledby={fieldId("delete-evidence-title")}
        PaperProps={{ sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 600 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minHeight: 56,
            px: 3,
            backgroundColor: AH_ACCENT,
          }}
        >
          <Typography id={fieldId("delete-evidence-title")} sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
            {pendingDelete?.kind === "comment" ? "Delete Comment" : pendingDelete?.isLink ? "Delete Link" : "Delete Artifact"}
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            disabled={deleteSaving}
            onClick={closeDeleteDialog}
            sx={{
              p: 0,
              m: 0,
              border: "none",
              background: "transparent",
              color: AH_WHITE,
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: deleteSaving ? "default" : "pointer",
            }}
          >
            ×
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
          <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6, overflowWrap: "anywhere" }}>
            Are you sure you want to permanently delete{" "}
            {pendingDelete?.name ? (
              <Box component="span" sx={{ fontWeight: 600 }}>
                {pendingDelete.name}
              </Box>
            ) : (
              `this ${deleteNoun}`
            )}
            ?
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
            This action cannot be undone. It will be removed from the{" "}
            {pendingDelete?.kind === "comment" ? "activity" : "evidence"} for {match.controlCode || "this control"}.
          </Typography>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: "1px solid #e5e7eb",
            backgroundColor: "#f8fafc",
          }}
        >
          <Button
            onClick={closeDeleteDialog}
            disabled={deleteSaving}
            sx={{
              ...confirmButtonSx,
              backgroundColor: "#74788d",
              color: "#eff2f7",
              "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          {deleteError ? (
            <Typography
              role="alert"
              sx={{
                flex: 1,
                minWidth: 0,
                order: { xs: 3, sm: 0 },
                flexBasis: { xs: "100%", sm: "auto" },
                fontSize: "0.8125rem",
                lineHeight: 1.45,
                color: "#dc2626",
                textAlign: "center",
                overflowWrap: "anywhere",
              }}
            >
              {deleteError}
            </Typography>
          ) : null}
          <Button
            onClick={confirmDelete}
            disabled={deleteSaving || !pendingDelete?.id}
            sx={{
              ...confirmButtonSx,
              backgroundColor: "#dc2626",
              color: AH_WHITE,
              "&:hover": { backgroundColor: "#b91c1c", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: "#dc2626", color: AH_WHITE, opacity: 0.65 },
            }}
          >
            {deleteSaving ? "Deleting" : "Delete"}
          </Button>
        </Box>
      </Dialog>

      <CreateTaskDialog
        open={taskOpen}
        onClose={() => setTaskOpen(false)}
        onCreated={onTaskCreated}
        auditId={roster.audit.auditId}
        auditControlId={match.auditControlId}
        controlCode={detail?.controlCode || match.controlCode}
        userOptions={userOptions}
        auditDue={auditDue}
      />

      <RequestInfoDialog
        open={requestInfoOpen}
        onClose={() => setRequestInfoOpen(false)}
        onDone={onRequestInfoDone}
        auditId={roster.audit.auditId}
        auditControlId={match.auditControlId}
        controlCode={detail?.controlCode || match.controlCode}
      />
    </Box>
    </AuditHubThemeProvider>
  );
};

export default ControlAuditPanel;
